import { useQueryUserRoles } from '@/services/users/queries'
import { OrgBusinessAccount } from '@/types/org'
import { canAssignRole } from '@/types/permissions'
import { forwardRef, useMemo } from 'react'

/* eslint-disable react-refresh/only-export-components -- hooks module */

type UsePermissionsProps = {
    businessAccount?: OrgBusinessAccount
    requiredPermissions: string[]
}

type UseAvailableRolesProps = {
    currentUserRole?: string
}

type PermissionsWrapperProps = {
    children: React.ReactNode
    businessAccount?: OrgBusinessAccount
    requiredPermissions: string[]
}

const usePermissions = ({ businessAccount, requiredPermissions }: UsePermissionsProps) => {
    if (!businessAccount) return { hasPermission: false }

    if (requiredPermissions.length === 0) return { hasPermission: true }

    const accountPermissions = businessAccount.role.permissions

    return {
        hasPermission: requiredPermissions.some(permission => accountPermissions.includes(permission)),
    }
}

export const useAvailableRoles = ({ currentUserRole }: UseAvailableRolesProps) => {
    const { data: userRoles } = useQueryUserRoles()

    const availableRoles = useMemo(() => {
        if (!userRoles || !currentUserRole) return []

        return userRoles.filter(role => {
            return canAssignRole(currentUserRole, role.name)
        })
    }, [userRoles, currentUserRole])

    return { availableRoles, allRoles: userRoles || [] }
}

export const PermissionsWrapper = forwardRef<HTMLElement, PermissionsWrapperProps>(
    ({ children, businessAccount, requiredPermissions }, ref) => {
        void ref
        const { hasPermission } = usePermissions({ businessAccount, requiredPermissions })

        if (!businessAccount) return null

        if (requiredPermissions.length === 0) return <>{children}</>

        return <>{hasPermission ? children : null}</>
    }
)
PermissionsWrapper.displayName = 'PermissionsWrapper'
